import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import PageWrapper from "@/components/layout/PageWrapper";

export default function About() {
  const { t, i18n } = useTranslation();
  const identityRef = useRef(null);
  const goalsRef = useRef(null);
  const whyRef = useRef(null);
  const ctaRef = useRef(null);
  const isRTL = i18n.language?.startsWith("ar");
  const phoneNumber = "966532785554";
  const whatsappMessage = isRTL
    ? "مرحباً، أنا مهتم بالتعرف أكثر على ابراج الرفوف وخدماتها."
    : "Hello, I am interested in learning more about Arafouf and its services.";
  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const identityInView = useInView(identityRef, { once: true, margin: "-80px" });
  const goalsInView = useInView(goalsRef, { once: true, margin: "-80px" });
  const whyInView = useInView(whyRef, { once: true, margin: "-80px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-80px" });

  const headlinePillars = [t("about.line1"), t("about.line2"), t("about.line3")];
  const whyIcons = [
    "lightbulb-line",
    "customer-service-2-line",
    "tools-line",
    "money-dollar-circle-line",
  ];

  return (
    <PageWrapper>
      {/* SEO */}
      <title>{t("meta.aboutTitle")}</title>
      <meta name="description" content={t("meta.aboutDesc")} />

      {/* Hero */}
      <section className="relative w-full min-h-[520px] md:min-h-[640px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src="/assets/images/17.jpg"
            alt="About Arafouf"
            className="w-full h-full object-cover object-top"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/15" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-14 md:pb-20 pt-36 md:pt-48">
          <motion.div
            className="max-w-5xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-2 text-xs md:text-sm text-white/90 mb-5">
              <i className="ri-shield-check-line" />
              {t("about.label")}
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.18] mb-5">
              {t("about.pageTitle")}
            </h1>
            <p className="text-lg md:text-2xl text-white/85 max-w-3xl leading-relaxed mb-7">
              {t("about.companyDesc")}
            </p>

            <div className="flex flex-wrap gap-3">
              {headlinePillars.map((pillar) => (
                <motion.span
                  key={pillar}
                  className="inline-flex items-center rounded-xl bg-brand/80 text-white px-4 py-2 text-sm md:text-base"
                  whileHover={{ y: -4, scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  {pillar}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Identity */}
      <section ref={identityRef} className="w-full py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <motion.div
              className="lg:col-span-7 space-y-6"
              initial={{ opacity: 0, x: isRTL ? 40 : -40 }}
              animate={identityInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? 40 : -40 }}
              transition={{ duration: 0.65 }}
            >
              <div className="rounded-2xl border border-stone-200 p-6 md:p-8 bg-white shadow-[0_14px_40px_rgba(0,0,0,0.04)]">
                <span className="text-sm font-medium text-brand uppercase tracking-wider mb-3 block">
                  {t("about.visionTitle")}
                </span>
                <h2 className="text-3xl md:text-5xl font-bold text-stone-900 leading-[1.45] md:leading-[1.4] mb-4">
                  {t("about.visionDesc")}
                </h2>
                <p className="text-base md:text-lg text-stone-600 leading-relaxed">
                  {t("about.subtitle")}
                </p>
              </div>

              <div className="rounded-2xl border border-stone-200 p-6 md:p-8 bg-[#F8F6F1]">
                <span className="text-sm font-medium text-brand uppercase tracking-wider mb-3 block">
                  {t("about.historySubtitle")}
                </span>
                <h3 className="text-2xl md:text-4xl font-bold text-stone-900 mb-4">
                  {t("about.historyTitle")}
                </h3>
                <p className="text-base md:text-lg text-stone-700 leading-relaxed">
                  {t("about.historyDesc")}
                </p>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
              animate={identityInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? -40 : 40 }}
              transition={{ duration: 0.65, delay: 0.1 }}
            >
              <div className="rounded-3xl overflow-hidden relative min-h-[360px] md:min-h-[460px]">
                <img
                  src="/assets/images/6.jpeg"
                  alt="Arafouf Journey"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent" />
                <div className="relative z-10 p-6 md:p-8 min-h-[360px] md:min-h-[460px] flex flex-col justify-end text-white">
                  <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-2">
                    {t("about.goalsTitle")}
                  </h3>
                  <p className="text-white/85 text-sm md:text-base leading-relaxed">
                    {t("about.goalsList.0")}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section ref={goalsRef} className="w-full py-16 md:py-24 bg-[#F5F3EF]">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <motion.div
            className="max-w-3xl mb-10 md:mb-14"
            initial={{ opacity: 0, y: 40 }}
            animate={goalsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium text-brand uppercase tracking-wider mb-4 block">
              {t("about.goalsTitle")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-stone-900 leading-[1.22]">
              {t("about.goalsTitle")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <motion.div
                key={i}
                className="relative bg-white rounded-2xl p-6 md:p-8 border border-stone-200 overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                animate={goalsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: 0.08 * i }}
              >
                <span className="absolute -top-3 ltr:-right-2 rtl:-left-2 text-7xl font-extrabold text-brand/10 pointer-events-none">
                  {`0${i + 1}`}
                </span>
                <div className="relative z-10 flex items-start gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-brand text-white rounded-full flex-shrink-0 font-bold text-sm">
                    {i + 1}
                  </span>
                  <p className="text-base md:text-lg text-stone-700 leading-relaxed">
                    {t(`about.goalsList.${i}`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section ref={whyRef} className="w-full py-16 md:py-24 bg-stone-900">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <motion.div
            className="max-w-3xl mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={whyInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium text-brand uppercase tracking-wider mb-3 block">
              {t("about.historySubtitle")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-[1.22]">
              {t("about.whyTitle")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <motion.div
                key={i}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 md:p-8"
                initial={{ opacity: 0, y: 40 }}
                animate={whyInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
              >
                <span className="w-12 h-12 flex items-center justify-center bg-brand/20 text-brand rounded-xl mb-4">
                  <i className={`ri-${whyIcons[i]} text-xl`} />
                </span>
                <h3 className="text-xl font-bold text-white mb-3">
                  {t(`about.whyCards.${i}.title`)}
                </h3>
                <p className="text-sm md:text-base text-stone-300 leading-relaxed">
                  {t(`about.whyCards.${i}.desc`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="w-full py-16 md:py-24 bg-brand-50">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <motion.div
            className="relative max-w-3xl mx-auto rounded-3xl border border-brand/20 bg-white px-6 py-10 md:px-10 md:py-12 shadow-[0_15px_45px_rgba(0,0,0,0.04)] overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
          >
            <motion.span
              className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-brand/10"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 text-brand px-4 py-2 text-xs md:text-sm font-semibold mb-4">
              <i className="ri-customer-service-2-line" />
              {t("services.talkServicesEyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              {t("services.talkToUs")}
            </h2>
            <p className="text-lg text-stone-600 mb-8">
              {t("services.talkDesc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/contact-us"
                className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-8 py-4 rounded-full hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("services.contactButton")}
                <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className={`${isRTL ? "ri-arrow-left-line" : "ri-arrow-right-line"} text-sm`} />
                </span>
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-4 rounded-full hover:bg-[#128C7E] transition-colors whitespace-nowrap cursor-pointer"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" />
                {t("services.whatsappButton")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageWrapper>
  );
}